import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { StreakService } from '../../services/StreakService';
import { XpService } from '../../services/XpService';
import { SoundEffectService } from '../../services/SoundEffectService';
import { LearningProgressService } from '../../services/LearningProgressService';
import { ChunkyButton, Confetti, StarRow, FloatingXpBadge } from '../../components/GameUI';

// Word-search puzzle — drag across letters to find each target-language
// word, hidden in a randomly generated grid (8 directions, letter overlaps
// allowed). Port of the mobile app's lib/games/word_search/{word_search_grid,
// word_search_screen}.dart. Selection is pointer-events based (mouse + touch)
// following the same technique as WordMatchGame, rather than native HTML5
// drag-and-drop.

// ---------------------------------------------------------------------------
// Grid generation — faithful port of word_search_grid.dart's
// generateWordSearch()/straightPath(). Cells are {x, y} (x = column,
// y = row), mirroring Dart's Point<int>(col, row).
// ---------------------------------------------------------------------------

const DIRECTIONS = [
  { x: 1, y: 0 }, { x: -1, y: 0 }, { x: 0, y: 1 }, { x: 0, y: -1 },
  { x: 1, y: 1 }, { x: 1, y: -1 }, { x: -1, y: 1 }, { x: -1, y: -1 },
];

// Valid start-index range along one axis for a word of `len` cells moving by
// `delta` per step, within [0, size). Returns null if it can't fit.
function validRange(size, delta, len) {
  if (delta === 0) return { min: 0, count: size };
  if (delta > 0) {
    const maxStart = size - len;
    if (maxStart < 0) return null;
    return { min: 0, count: maxStart + 1 };
  }
  const minStart = len - 1;
  if (minStart > size - 1) return null;
  return { min: minStart, count: size - minStart };
}

/**
 * Places `words` into a random letter-search grid (8 directions, allowing
 * letter-matching overlaps), retrying each word up to `maxAttempts` times
 * before giving up on it. Words that can't fit are simply dropped — the
 * caller ends up with however many of `words` actually placed.
 */
function generateWordSearch(words, { size, maxAttempts = 250, rng = Math.random } = {}) {
  const longest = words.reduce((m, w) => Math.max(m, w.length), 0);
  const gridSize = size || Math.min(12, Math.max(8, longest + 2));
  const grid = Array.from({ length: gridSize }, () => Array(gridSize).fill(''));
  const placed = [];

  words.forEach((rawWord, wordIdx) => {
    const word = String(rawWord).toUpperCase();
    const letters = Array.from(word);
    const len = letters.length;
    if (len === 0 || len > gridSize) return;

    let success = false;
    for (let attempt = 0; attempt < maxAttempts && !success; attempt++) {
      const dir = DIRECTIONS[Math.floor(rng() * DIRECTIONS.length)];
      const rowRange = validRange(gridSize, dir.y, len);
      const colRange = validRange(gridSize, dir.x, len);
      if (!rowRange || !colRange) continue;
      const startRow = rowRange.min + Math.floor(rng() * rowRange.count);
      const startCol = colRange.min + Math.floor(rng() * colRange.count);

      const cells = [];
      let fits = true;
      for (let i = 0; i < len; i++) {
        const r = startRow + dir.y * i;
        const c = startCol + dir.x * i;
        const existing = grid[r][c];
        if (existing && existing !== letters[i]) { fits = false; break; }
        cells.push({ x: c, y: r });
      }
      if (!fits) continue;
      cells.forEach((cell, i) => { grid[cell.y][cell.x] = letters[i]; });
      placed.push({ id: `${word}_${wordIdx}`, word, cells });
      success = true;
    }
  });

  const pool = new Set();
  placed.forEach((p) => Array.from(p.word).forEach((ch) => pool.add(ch)));
  if (pool.size === 0) pool.add('X');
  const poolList = Array.from(pool);
  for (let r = 0; r < gridSize; r++) {
    for (let c = 0; c < gridSize; c++) {
      if (!grid[r][c]) grid[r][c] = poolList[Math.floor(rng() * poolList.length)];
    }
  }

  return { size: gridSize, letters: grid, words: placed };
}

// The straight-line (horizontal/vertical/diagonal) path of cells between
// `start` and `end` inclusive, or null if they don't form a straight line.
function straightPath(start, end) {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  if (dx === 0 && dy === 0) return [start];
  if (dx !== 0 && dy !== 0 && Math.abs(dx) !== Math.abs(dy)) return null;
  const stepX = dx === 0 ? 0 : dx > 0 ? 1 : -1;
  const stepY = dy === 0 ? 0 : dy > 0 ? 1 : -1;
  const steps = Math.max(Math.abs(dx), Math.abs(dy));
  return Array.from({ length: steps + 1 }, (_, i) => ({ x: start.x + stepX * i, y: start.y + stepY * i }));
}

function sameCells(a, b) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].x !== b[i].x || a[i].y !== b[i].y) return false;
  }
  return true;
}

function elapsedLabel(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

// ---------------------------------------------------------------------------

export default function WordSearchGame() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { words = [], targetLang = { code: '', label: '', dir: 'ltr' }, langPair = 'guest' } = state || {};

  const [puzzle, setPuzzle] = useState(() => generateWordSearch(words));
  const [foundIds, setFoundIds] = useState(() => new Set());
  const [dragStartCell, setDragStartCell] = useState(null);
  const [selectedPath, setSelectedPath] = useState([]);
  const [selectionWrong, setSelectionWrong] = useState(false);
  const [hintedCells, setHintedCells] = useState(() => new Set());
  const [hintsUsed, setHintsUsed] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [done, setDone] = useState(false);

  const gridRef = useRef(null);
  const [cellPx, setCellPx] = useState(40);
  const rewardedRef = useRef(false);
  const hintTimeoutRef = useRef(null);
  const wrongTimeoutRef = useRef(null);

  const invalid = !words.length || puzzle.words.length === 0;

  useEffect(() => {
    if (invalid) navigate('/games', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ticking clock — paused once the puzzle is solved.
  useEffect(() => {
    if (done || invalid) return undefined;
    const id = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  // Track the grid's rendered cell size so letters scale like the mobile
  // app's `(cellSize * 0.45).clamp(10, 20)`.
  useEffect(() => {
    const el = gridRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w) setCellPx(w / puzzle.size);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [puzzle.size]);

  useEffect(() => () => {
    clearTimeout(hintTimeoutRef.current);
    clearTimeout(wrongTimeoutRef.current);
  }, []);

  const stars = hintsUsed === 0 ? 3 : hintsUsed <= 2 ? 2 : 1;
  const xpEarned = puzzle.words.length * 5 + (stars === 3 ? 10 : 0);

  function awardRewards() {
    if (rewardedRef.current) return;
    rewardedRef.current = true;
    StreakService.recordActivity();
    XpService.addXp(xpEarned);
    if (stars === 3) SoundEffectService.playCelebration();
  }

  useEffect(() => {
    if (done) awardRewards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  function restart() {
    clearTimeout(hintTimeoutRef.current);
    clearTimeout(wrongTimeoutRef.current);
    setPuzzle(generateWordSearch(words));
    setFoundIds(new Set());
    setDragStartCell(null);
    setSelectedPath([]);
    setSelectionWrong(false);
    setHintedCells(new Set());
    setHintsUsed(0);
    setElapsed(0);
    setDone(false);
    rewardedRef.current = false;
  }

  const foundCellKeys = useMemo(() => {
    const set = new Set();
    puzzle.words.forEach((w) => {
      if (foundIds.has(w.id)) w.cells.forEach((c) => set.add(`${c.x},${c.y}`));
    });
    return set;
  }, [puzzle, foundIds]);

  const selectedKeys = useMemo(
    () => new Set(selectedPath.map((c) => `${c.x},${c.y}`)),
    [selectedPath],
  );

  function cellAt(clientX, clientY) {
    const rect = gridRef.current.getBoundingClientRect();
    const size = rect.width / puzzle.size;
    const col = Math.min(puzzle.size - 1, Math.max(0, Math.floor((clientX - rect.left) / size)));
    const row = Math.min(puzzle.size - 1, Math.max(0, Math.floor((clientY - rect.top) / size)));
    return { x: col, y: row };
  }

  function finalizeSelection(path) {
    if (path.length > 1) {
      const reversed = [...path].reverse();
      const match = puzzle.words.find(
        (w) => !foundIds.has(w.id) && (sameCells(w.cells, path) || sameCells(w.cells, reversed)),
      );
      if (match) {
        SoundEffectService.playCorrect();
        if (navigator.vibrate) navigator.vibrate(15);
        setSelectedPath([]);
        setSelectionWrong(false);
        setFoundIds((prev) => {
          const next = new Set(prev);
          next.add(match.id);
          if (next.size === puzzle.words.length) setDone(true);
          return next;
        });
        LearningProgressService.record(langPair, 'vocabulary', 1, { activityId: `wordsearch:${match.word}` });
        return;
      }
      setSelectionWrong(true);
      SoundEffectService.playWrong();
      wrongTimeoutRef.current = setTimeout(() => {
        setSelectedPath([]);
        setSelectionWrong(false);
      }, 250);
      return;
    }
    setSelectedPath([]);
  }

  function handleDragStart(e) {
    if (done) return;
    const point = e.touches ? e.touches[0] : e;
    const startCell = cellAt(point.clientX, point.clientY);
    let currentPath = [startCell];
    setDragStartCell(startCell);
    setSelectedPath(currentPath);
    setSelectionWrong(false);

    function onMove(ev) {
      const p = ev.touches ? ev.touches[0] : ev;
      const cell = cellAt(p.clientX, p.clientY);
      const path = straightPath(startCell, cell);
      if (path) {
        currentPath = path;
        setSelectedPath(path);
      }
    }
    function onUp() {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
      setDragStartCell(null);
      finalizeSelection(currentPath);
    }
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
  }

  function giveHint() {
    const unfound = puzzle.words.filter((w) => !foundIds.has(w.id));
    if (!unfound.length) return;
    const word = unfound[Math.floor(Math.random() * unfound.length)];
    setHintedCells(new Set(word.cells.map((c) => `${c.x},${c.y}`)));
    setHintsUsed((h) => h + 1);
    clearTimeout(hintTimeoutRef.current);
    hintTimeoutRef.current = setTimeout(() => setHintedCells(new Set()), 1200);
  }

  if (invalid) return null;

  const foundCount = foundIds.size;
  const fontSize = Math.min(20, Math.max(10, cellPx * 0.45));

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-lg px-4 py-4 sm:px-6">
        <div className="mb-4 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-500 shadow-sm hover:bg-slate-50"
          >
            ← {t('games.title')}
          </button>
          <h1 className="text-sm font-black text-slate-900">{t('games.wordSearch')}</h1>
          {!done ? (
            <button
              onClick={giveHint}
              disabled={foundCount === puzzle.words.length}
              className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-bold text-amber-700 shadow-sm hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-40"
            >
              💡 {t('games.wordSearchHint', 'Hint')}
            </button>
          ) : (
            <span className="w-16" />
          )}
        </div>

        {done ? (
          <div className="relative flex flex-col items-center py-10 text-center">
            {stars === 3 && <Confetti />}
            <span className="text-6xl">{stars === 3 ? '🏆' : '🎉'}</span>
            <h2 className="mt-3 text-2xl font-black text-slate-900">
              {t('games.wordSearchComplete', 'Puzzle complete!')}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              {elapsedLabel(elapsed)} • {puzzle.words.length} {t('games.wordSearchWordsLabel', 'words')}
            </p>
            <div className="mt-3">
              <StarRow count={stars} />
            </div>
            <div className="mt-3">
              <FloatingXpBadge xp={xpEarned} />
            </div>
            <div className="mt-8 w-full max-w-xs">
              <ChunkyButton
                color="#4F46E5"
                onClick={restart}
                className="w-full py-4 text-center text-white"
              >
                {t('games.tryAgain')}
              </ChunkyButton>
              <button
                onClick={() => navigate('/games')}
                className="mt-3 w-full rounded-2xl border border-slate-200 py-3 text-sm font-bold text-slate-600 hover:bg-slate-50"
              >
                {t('games.done')}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-base font-black text-slate-900">
                {foundCount}/{puzzle.words.length}
              </span>
              <span className="flex items-center gap-1 text-xs font-bold text-slate-500">
                ⏱ {elapsedLabel(elapsed)}
              </span>
            </div>

            <div className="mb-4 flex flex-wrap gap-1.5" dir={targetLang.dir}>
              {puzzle.words.map((w) => {
                const isFound = foundIds.has(w.id);
                return (
                  <span
                    key={w.id}
                    className={`rounded-full border px-2.5 py-1 text-xs font-extrabold ${
                      isFound
                        ? 'border-[#58CC02] bg-[#D7FFB8] text-[#2B6B00] line-through'
                        : 'border-slate-200 bg-slate-100 text-slate-600'
                    }`}
                  >
                    {w.word}
                  </span>
                );
              })}
            </div>

            <div className="flex justify-center">
              <div
                ref={gridRef}
                onPointerDown={handleDragStart}
                onTouchStart={handleDragStart}
                className="grid aspect-square w-full max-w-sm select-none rounded-2xl border border-slate-200 bg-white shadow-sm"
                style={{
                  gridTemplateColumns: `repeat(${puzzle.size}, 1fr)`,
                  gridTemplateRows: `repeat(${puzzle.size}, 1fr)`,
                  touchAction: 'none',
                  cursor: dragStartCell ? 'grabbing' : 'crosshair',
                }}
              >
                {puzzle.letters.map((rowArr, row) =>
                  rowArr.map((letter, col) => {
                    const key = `${col},${row}`;
                    const foundHere = foundCellKeys.has(key);
                    const selected = selectedKeys.has(key);
                    const hinted = hintedCells.has(key);

                    let bg = 'transparent';
                    let fg = '#0F172A';
                    if (foundHere) {
                      bg = '#D7FFB8';
                      fg = '#2B6B00';
                    } else if (selected) {
                      bg = selectionWrong ? '#FFDFE0' : 'rgba(79,70,229,0.25)';
                      fg = selectionWrong ? '#8B0000' : '#4F46E5';
                    } else if (hinted) {
                      bg = '#FFF3C4';
                      fg = '#92400E';
                    }

                    return (
                      <div
                        key={key}
                        className="flex items-center justify-center"
                        style={{ background: bg, color: fg, fontWeight: 800, fontSize }}
                      >
                        {letter}
                      </div>
                    );
                  }),
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
